// contexts/PreferencesContext.js
import React, { createContext, useContext, useReducer, useEffect } from 'react';
import preferencesService from '../services/preferencesService';
import { useAuth } from './AuthContext';

const PreferencesContext = createContext();

// Default preferences
const defaultPreferences = {
  calendarToggles: {
    showIncome: true,
    showExpenses: true,
    showTransfers: true,
    showBudgets: true
  },
  budgetViewType: 'matrix',
  actionsViewType: 'list',
  dashboardLayout: 'expanded',
  theme: 'auto',
  weekStart: 'monday'
};

// Local storage key for preferences cache
const PREFERENCES_STORAGE_KEY = 'userPreferences';

// Helper function to get preferences from local storage
const getStoredPreferences = () => {
  try {
    const stored = localStorage.getItem(PREFERENCES_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading preferences from localStorage:', error);
    return null;
  }
};

// Helper function to store preferences in local storage
const storePreferences = (preferences) => {
  try {
    localStorage.setItem(PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
  } catch (error) {
    console.error('Error storing preferences to localStorage:', error);
  }
};

// Reducer for preferences state management
const preferencesReducer = (state, action) => {
  switch (action.type) {
    case 'SET_PREFERENCES':
      return { ...state, ...action.payload };
    
    case 'UPDATE_CALENDAR_TOGGLES':
      return {
        ...state,
        calendarToggles: { ...state.calendarToggles, ...action.payload }
      };
    
    case 'UPDATE_BUDGET_VIEW':
      return { ...state, budgetViewType: action.payload };
    
    case 'UPDATE_ACTIONS_VIEW':
      return { ...state, actionsViewType: action.payload };
    
    case 'UPDATE_DASHBOARD_LAYOUT':
      return { ...state, dashboardLayout: action.payload };
    
    case 'UPDATE_THEME':
      return { ...state, theme: action.payload };
    
    case 'RESET_PREFERENCES':
      return defaultPreferences;
    
    default:
      return state;
  }
};

export const PreferencesProvider = ({ children }) => {
  // Initialize with stored preferences if available
  const initializePreferences = () => {
    const storedPrefs = getStoredPreferences();
    return storedPrefs || defaultPreferences;
  };

  const [preferences, dispatch] = useReducer(preferencesReducer, initializePreferences());
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [isInitialized, setIsInitialized] = React.useState(false);
  const { user } = useAuth();

  // Store preferences in localStorage whenever they change
  useEffect(() => {
    if (isInitialized) {
      storePreferences(preferences);
    }
  }, [preferences, isInitialized]);

  // Load preferences when user logs in and keep them in sync
  useEffect(() => {
    const loadPreferences = async () => {
      if (!user) {
        setLoading(false);
        setIsInitialized(false);
        dispatch({ type: 'RESET_PREFERENCES' });
        localStorage.removeItem(PREFERENCES_STORAGE_KEY);
        return;
      }

      // Don't reload if already initialized for this user session
      if (isInitialized) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const userPrefs = await preferencesService.getUserPreferences();
        dispatch({ type: 'SET_PREFERENCES', payload: userPrefs });
        setError(null);
        setIsInitialized(true);
      } catch (err) {
        console.error('Failed to load user preferences:', err);
        setError('Failed to load preferences');
        // Use stored preferences or defaults on error
        const storedPrefs = getStoredPreferences();
        if (storedPrefs) {
          dispatch({ type: 'SET_PREFERENCES', payload: storedPrefs });
        } else {
          dispatch({ type: 'RESET_PREFERENCES' });
        }
        setIsInitialized(true);
      } finally {
        setLoading(false);
      }
    };

    loadPreferences();
  }, [user, isInitialized]);

  // Update calendar toggles
  const updateCalendarToggles = async (toggles) => {
    // Optimistic update for immediate UI response
    dispatch({ type: 'UPDATE_CALENDAR_TOGGLES', payload: toggles });
    
    try {
      const updatedToggles = await preferencesService.updateCalendarToggles(toggles);
      // Ensure we have the server response (in case of any server-side modifications)
      dispatch({ type: 'UPDATE_CALENDAR_TOGGLES', payload: updatedToggles });
      return updatedToggles;
    } catch (error) {
      console.error('Failed to update calendar toggles:', error);
      // Revert the optimistic update on error
      const currentPrefs = getStoredPreferences() || defaultPreferences;
      dispatch({ type: 'UPDATE_CALENDAR_TOGGLES', payload: currentPrefs.calendarToggles });
      throw error;
    }
  };

  // Update budget view type
  const updateBudgetViewType = async (viewType) => {
    // Optimistic update
    dispatch({ type: 'UPDATE_BUDGET_VIEW', payload: viewType });
    
    try {
      await preferencesService.updateBudgetViewType(viewType);
      return viewType;
    } catch (error) {
      console.error('Failed to update budget view type:', error);
      // Revert on error
      const currentPrefs = getStoredPreferences() || defaultPreferences;
      dispatch({ type: 'UPDATE_BUDGET_VIEW', payload: currentPrefs.budgetViewType });
      throw error;
    }
  };

  // Update actions view type
  const updateActionsViewType = async (viewType) => {
    // Optimistic update
    dispatch({ type: 'UPDATE_ACTIONS_VIEW', payload: viewType });
    
    try {
      await preferencesService.updateActionsViewType(viewType);
      return viewType;
    } catch (error) {
      console.error('Failed to update actions view type:', error);
      // Revert on error
      const currentPrefs = getStoredPreferences() || defaultPreferences;
      dispatch({ type: 'UPDATE_ACTIONS_VIEW', payload: currentPrefs.actionsViewType });
      throw error;
    }
  };

  // Toggle specific calendar item
  const toggleCalendarItem = async (itemName, isVisible) => {
    try {
      const toggleUpdate = { [itemName]: isVisible };
      const updatedToggles = await preferencesService.updateCalendarToggles(toggleUpdate);
      dispatch({ type: 'UPDATE_CALENDAR_TOGGLES', payload: updatedToggles });
      return updatedToggles;
    } catch (error) {
      console.error(`Failed to toggle calendar ${itemName}:`, error);
      throw error;
    }
  };

  // Update multiple preferences at once
  const updatePreferences = async (updates) => {
    try {
      const updatedPrefs = await preferencesService.updatePreferences(updates);
      dispatch({ type: 'SET_PREFERENCES', payload: updatedPrefs });
      return updatedPrefs;
    } catch (error) {
      console.error('Failed to update preferences:', error);
      throw error;
    }
  };

  const value = {
    preferences,
    loading,
    error,
    isInitialized,
    updateCalendarToggles,
    updateBudgetViewType,
    updateActionsViewType,
    toggleCalendarItem,
    updatePreferences,
  };

  return (
    <PreferencesContext.Provider value={value}>
      {children}
    </PreferencesContext.Provider>
  );
};

// Custom hook for using preferences
export const usePreferences = () => {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
};

// Custom hook that guarantees preferences are available (uses defaults if not loaded)
export const usePreferencesReady = () => {
  const { preferences, loading, error, isInitialized, ...actions } = usePreferences();
  
  // Always return preferences (either loaded or defaults)
  const readyPreferences = React.useMemo(() => {
    if (!isInitialized && loading) {
      // If still loading and not initialized, check localStorage first
      const stored = getStoredPreferences();
      return stored || defaultPreferences;
    }
    return preferences;
  }, [preferences, loading, isInitialized]);

  return {
    preferences: readyPreferences,
    loading,
    error,
    isInitialized,
    ...actions
  };
};
